import * as React from 'react';
import styled from 'styled-components';
import * as L from 'leaflet';
import * as uuid from 'uuid';
// @ts-ignore
import 'leaflet-draw/dist/leaflet.draw';
// @ts-ignore
const s = require('leaflet-draw/dist/leaflet.draw.css');

type LatLong = {
  lat: number;
  lng: number;
}

type StaticMarker = LatLong & {
  title: string;
  image: string;
}

/**
 * @property {function} [onFeatureEdited] - Triggered when a feature is edited.
 * @property {function} [onFeatureCreated] - Triggered when a feature is created.
 * @property {function} [onMarkerPlaced] - Triggered when a marker is placed.
 * @property {StaticMarker[]} [staticMarkers] - Static markers to put onto the map that cannot be moved or edited.
 * These markers can include titles and images. (Mostly Point of Interest)
 * @property {Object} geoJson - GeoJSON object to initialize onto the map.
 * @property {Boolean} controls - Should the controls be displayed on the map.
 * @property {Boolean} clearOnRedraw - Should the canvas clear when a user wants to draw another object.
 * @property {Boolean} showMarker - Show the moveable marker on the map.
 * @property {L.LatLng} markerPosition - Initial position of the marker that can be moved on the map.
 * @property {Object} drawOptions - Leaflet Draw Options to initialize the Draw object with:
 * http://leaflet.github.io/Leaflet.draw/docs/leaflet-draw-latest.html#options
 */
type Props = {
  onFeatureEdited?: (geoJson: L.GeoJSON, distance?: number) => void;
  onFeatureCreated?: (geoJson: L.GeoJSON, distance?: number) => void;
  onMarkerPlaced?: (latLng: L.LatLng) => void;
  staticMarkers?: StaticMarker[];
  geoJson?: any;
  controls?: boolean;
  clearOnRedraw?: boolean;
  showMarker?: boolean;
  markerPosition?: LatLong;
  drawOptions?: any;
}

const LeafletMapContainer = styled.div`
  height: 100%;
  width: 100%;
`;

/**
 * **IMPORTANT** Wrap this Leaflet map in a container with a WIDTH and HEIGHT.
 * Initializes a new leaflet map.
 */
const LeafletMap: React.FC<Props> = ({
  onFeatureCreated,
  onFeatureEdited,
  onMarkerPlaced,
  clearOnRedraw,
  geoJson,
  staticMarkers,
  controls,
  showMarker,
  markerPosition,
  drawOptions,
}) => {
  let mapMarker: L.Marker;
  let map: L.Map;
  const editableLayers: L.FeatureGroup = new L.FeatureGroup();
  const mapId = uuid();
  const [isInit, setIsInit] = React.useState(false);

  /**
   * Insert leafletMap into the dom
   * @param latitude 
   * @param longitude 
   */
  const initializeMap = (lng: number, lat: number) => {
    map = L.map(mapId).setView({lng, lat}, 13);
    L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      subdomains: ['a', 'b', 'c']
    }).addTo(map);
    map.addLayer(editableLayers);

  }

  /**
   * Add map features based on GeoJSON provided.
   */
  const initializeMapFeatures = () => {
    if (geoJson) {
      // @ts-ignore
      const features = L.geoJson(geoJson);
      features.getLayers().forEach((layer: L.Layer) => {
        editableLayers.addLayer(layer);
      });
    }
  }

  /**
   * Add a marker to the map with the specified lat and lng.
   * @param {number} lng - Longitude of marker.
   * @param {number} lat - Latitude of marker.
   * @param {string} title - Title to show on marker.
   * @param {string} image - Image to display on marker.
   */
  const addMapMarker = (lng: number, lat: number, title?: string, image?: string) => {
    const marker = L.marker({lng, lat});
    if (title) {
      marker.bindPopup(`
      <div style="display: flex; flex-direction: column; align-items: center;">
        ${image ? `<image src="${image}" title="${title}" height="50px" width="50px"/>` : ''}
        <span>${title}</span>
      </div>`
      );
    }
    marker.addTo(map);
    return marker;
  }

  /**
   * Create all the static markers.
   */
  const initializeStaticMarkers = () => {
    if (staticMarkers && staticMarkers.length) {
      staticMarkers.forEach(({lng, lat, title, image }: StaticMarker) => {
        addMapMarker(lng, lat, title, image);
      })
    }
  }

  /**
   * Create and configure the leaflet draw controls.
   */
  const addMapControls = () => {
    const options = drawOptions;
    options.edit.featureGroup = editableLayers;

    // @ts-ignore
    const drawControl = new L.Control.Draw(options);
    map.addControl(drawControl);
  }

  const addEventListeners = () => {
    /**
     * Clear the canvas if the user redraws.
     */
    map.on('draw:drawstart', () => {
      if (clearOnRedraw) {
        editableLayers.clearLayers();
      }
    });

    /**
     * Emit the created feature when the user has completed drawing.
     */
    map.on('draw:created', (e: L.LeafletEvent) => {
      const layer = e.layer;
      editableLayers.addLayer(layer);
      if (onFeatureCreated) {
        const distance = Number.parseFloat((layer._latlngs.reduce(
          (acc: any, curr: any, idx: any, arr: any) =>
            (acc + curr.distanceTo(arr[idx + 1 <= arr.length - 1 ? idx + 1 : idx])), 0) / 1000).toFixed(2));
        onFeatureCreated(layer.toGeoJSON(), distance);
      }
    });

    /**
     * Emit the edited feature when the user has completed drawing.
     */
    map.on('draw:edited', function (e: any) {
      if (onFeatureEdited) {
        const layers = e.layers;
        // @ts-ignore
        layers.eachLayer(function (layer: any) {
          // do whatever you want; most likely save back to db
          const distance = Number.parseFloat((layer._latlngs.reduce(
            (acc: any, curr: any, idx: any, arr: any) =>
              (acc + curr.distanceTo(arr[idx + 1 <= arr.length - 1 ? idx + 1 : idx])), 0) / 1000).toFixed(2));
          onFeatureEdited(layer.toGeoJSON(), distance);
        });
      }
    });

    /**
     * Emit the new latitude and longitude of the marker when it is moved.
     */
    map.on('click', function (e: L.LeafletMouseEvent) {
      if (showMarker) {
        if (mapMarker) {
          mapMarker.remove();
        }
        const location: L.LatLng = e.latlng;
        mapMarker = addMapMarker(location.lng, location.lat);
        if (onMarkerPlaced) {
          onMarkerPlaced(location);
        }
      }
    });
    setIsInit(true);
  }

  React.useEffect(() => {
    // TODO: Investigate why window.navigator doesn't work on certain browsers
    // window.navigator.geolocation.getCurrentPosition(location => {
    // const { latitude, longitude } = location.coords;
    if (!isInit) {
      // @ts-ignore
      initializeMap(markerPosition!.lng, markerPosition!.lat);
      initializeMapFeatures();
      initializeStaticMarkers();
      if (controls) {
        addMapControls();
      }
      addEventListeners();
      if (markerPosition && showMarker) {
        mapMarker = addMapMarker(markerPosition.lng, markerPosition.lat);
      }
    }
    // });
  });

  return (
    <LeafletMapContainer id={mapId} />
  )
};

LeafletMap.defaultProps = {
  showMarker: true,
  clearOnRedraw: true,
  controls: true,
  drawOptions: {
    position: 'topleft',
    draw: {
      polygon: false,
      circle: false,
      circlemarker: false,
      rectangle: false,
      marker: false,
    },
    edit: {
      remove: true
    }
  }
};

export default LeafletMap;



// WEBPACK FOOTER //
// ./src/components/Leaflet.tsx